import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { SiteConfigProvider } from "@/context/SiteConfigContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCTA from "@/components/MobileCTA";
import ChatWidget from "@/components/ChatWidget";
import PromoPopup from "@/components/PromoPopup";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Gallery from "@/pages/Gallery";
import Testimonials from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import LocationPage from "@/pages/LocationPage";
import Locations from "@/pages/Locations";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SMSPolicy from "@/pages/SMSPolicy";
import SitemapPage from "@/pages/SitemapPage";
import ServiceLandingPage from "@/pages/ServiceLandingPage";
import CommunityDiscount from "@/pages/CommunityDiscount";
import ProtectedRoute from "@/components/ProtectedRoute";
import SEOHead from "@/components/SEOHead";

// Redirect www to non-www for SEO (canonical URL enforcement)
if (typeof window !== 'undefined' && window.location.hostname.startsWith('www.')) {
  window.location.href = window.location.href.replace('www.', '');
}

// Lazy load admin pages to reduce initial bundle size
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminCustomers = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminGallery = lazy(() => import("@/pages/admin/AdminGallery"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminCampaigns = lazy(() => import("@/pages/admin/AdminCampaigns"));
const AdminCMS = lazy(() => import("@/pages/admin/AdminCMS"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminLandingPages = lazy(() => import("@/pages/admin/AdminLandingPages"));
const AdminPressReleases = lazy(() => import("@/pages/admin/AdminPressReleases"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminCredentials = lazy(() => import("@/pages/admin/AdminCredentials"));
const AdminHelp = lazy(() => import("@/pages/admin/AdminHelp"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminSMSConversations = lazy(() => import("@/pages/admin/AdminSMSConversations"));
const AdminVideos = lazy(() => import("@/pages/admin/AdminVideos"));
const AdminProjects = lazy(() => import("@/pages/admin/AdminProjects"));
const AdminTemplates = lazy(() => import("@/pages/admin/AdminTemplates"));
const AdminGBP = lazy(() => import("@/pages/admin/AdminGBP"));
const AdminServicePages = lazy(() => import("@/pages/admin/AdminServicePages"));
const AdminQRGenerator = lazy(() => import("@/pages/admin/AdminQRGenerator"));
const AdminIndexing = lazy(() => import("@/pages/admin/AdminIndexing"));
const AdminBilling = lazy(() => import("@/pages/admin/AdminBilling"));
const AdminDiscount = lazy(() => import("@/pages/admin/AdminDiscount"));

// Loading fallback for lazy components
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
    <div className="text-white text-lg">Loading...</div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <SiteConfigProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PromoPopup />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<><SEOHead /><Header /><Home /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/about" element={<><SEOHead title="About Us" /><Header /><About /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/services" element={<><SEOHead title="Services" /><Header /><Services /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/services/:slug" element={<><Header /><ServiceLandingPage /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/gallery" element={<><SEOHead title="Gallery" /><Header /><Gallery /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/testimonials" element={<><SEOHead title="Testimonials" /><Header /><Testimonials /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/contact" element={<><SEOHead title="Contact" /><Header /><Contact /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/blog" element={<><SEOHead title="Blog" /><Header /><Blog /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/blog/:slug" element={<><Header /><BlogPost /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/locations" element={<><SEOHead title="Service Areas" /><Header /><Locations /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/locations/:slug" element={<><Header /><LocationPage /><Footer /><MobileCTA /><ChatWidget /></>} />
            <Route path="/privacy-policy" element={<><SEOHead title="Privacy Policy" /><Header /><PrivacyPolicy /><Footer /></>} />
            <Route path="/sms-policy" element={<><SEOHead title="SMS Policy" /><Header /><SMSPolicy /><Footer /></>} />
            <Route path="/sitemap" element={<><Header /><SitemapPage /><Footer /></>} />
            <Route path="/community-appreciation-discount" element={<><Header /><CommunityDiscount /><Footer /><MobileCTA /><ChatWidget /></>} />            
            {/* Admin Routes - Lazy loaded for better performance */}
            <Route path="/admin/login" element={<Suspense fallback={<LoadingFallback />}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminDashboard /></Suspense></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminContacts /></Suspense></ProtectedRoute>} />
            <Route path="/admin/customers" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminCustomers /></Suspense></ProtectedRoute>} />
            <Route path="/admin/gallery" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminGallery /></Suspense></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminTestimonials /></Suspense></ProtectedRoute>} />
            <Route path="/admin/campaigns" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminCampaigns /></Suspense></ProtectedRoute>} />
            <Route path="/admin/cms" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminCMS /></Suspense></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminBlog /></Suspense></ProtectedRoute>} />
            <Route path="/admin/landing-pages" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminLandingPages /></Suspense></ProtectedRoute>} />
            <Route path="/admin/press-releases" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminPressReleases /></Suspense></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminSEO /></Suspense></ProtectedRoute>} />
            <Route path="/admin/credentials" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminCredentials /></Suspense></ProtectedRoute>} />
            <Route path="/admin/help" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminHelp /></Suspense></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminSettings /></Suspense></ProtectedRoute>} />
            <Route path="/admin/sms-conversations" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminSMSConversations /></Suspense></ProtectedRoute>} />
            <Route path="/admin/videos" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminVideos /></Suspense></ProtectedRoute>} />
            <Route path="/admin/projects" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminProjects /></Suspense></ProtectedRoute>} />
            <Route path="/admin/templates" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminTemplates /></Suspense></ProtectedRoute>} />
            <Route path="/admin/gbp" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminGBP /></Suspense></ProtectedRoute>} />
            <Route path="/admin/service-pages" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminServicePages /></Suspense></ProtectedRoute>} />
            <Route path="/admin/qr-generator" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminQRGenerator /></Suspense></ProtectedRoute>} />
            <Route path="/admin/indexing" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminIndexing /></Suspense></ProtectedRoute>} />
            <Route path="/admin/billing" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminBilling /></Suspense></ProtectedRoute>} />
            <Route path="/admin/discount" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdminDiscount /></Suspense></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </SiteConfigProvider>
    </div>
  );
}

export default App;
